import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Sliders } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ThresholdDialog({ meterId, current = 2.5, onSaved, disabled }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { if (open) setValue(current); }, [open, current]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/thresholds/${meterId}`, { threshold: value });
      toast.success(`Soglia Z ${meterId} = ${value.toFixed(2)}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore soglia");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          disabled={disabled}
          data-testid={`threshold-btn-${meterId}`}
          className="text-slate-500 hover:text-[#3EB6F0] transition-colors disabled:opacity-30"
          title="Regola soglia Z-Score"
        >
          <Sliders size={14} weight="bold" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-md" data-testid="threshold-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Soglia Z-Score · <span className="font-mono text-[#3EB6F0]">{meterId}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Soglia anomalia</span>
              <span className="font-mono text-3xl font-semibold text-[#3EB6F0]" data-testid="threshold-value">{value.toFixed(2)}</span>
            </div>
            <Slider
              value={[value]}
              min={0.5} max={6} step={0.1}
              onValueChange={(v) => setValue(v[0])}
              data-testid="threshold-slider"
            />
            <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-2">
              <span>0.5 (sensibile)</span>
              <span>2.5 (default)</span>
              <span>6.0 (permissivo)</span>
            </div>
          </div>
          <div className="text-xs text-slate-400 leading-relaxed border border-slate-800 rounded-md p-3 bg-[#0a0f1c]">
            <strong className="text-slate-200">Come funziona:</strong> il motore ML segnala un'anomalia quando
            il valore assoluto dello Z-Score supera la soglia. Valori bassi = più allarmi, valori alti = solo
            outlier estremi.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="threshold-save-button"
            className="bg-[#3EB6F0] text-[#070B14] hover:bg-[#2CA4DE]">
            {saving ? "Salva..." : "Applica soglia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
